import { DragEvent, useEffect, useMemo, useState } from 'react'
import { QuickKeyBlank, QuickKeyFolder, QuickKeyKey } from './utils/qk-types'
import { QuickKeyNodeType } from './utils/qk.constants'
import {
  DragStatus,
  HandleDropParams,
  isValidDropDestination,
} from './quickKeyUtils'

interface UseDragEventsParams {
  quickKey: QuickKeyKey | QuickKeyFolder | QuickKeyBlank
  containerElement: HTMLElement | null
  handleDrop?: (params: HandleDropParams) => void
}

interface DragEvents {
  onDragStart?: (e: DragEvent) => void
  onDragEnter?: (e: DragEvent) => void
  onDragLeave?: (e: DragEvent) => void
  onDragOver?: (e: DragEvent) => void
  onDrop?: (e: DragEvent) => void
  dragStatus?: DragStatus | null
}

let draggedQuickKey: QuickKeyKey | QuickKeyFolder | null = null
let listenerCount = 0

const dropEventListener = () => {
  draggedQuickKey = null
}

const register = () => {
  if (listenerCount === 0) {
    document.addEventListener('drop', dropEventListener)
  }
  listenerCount++
}

const unregister = () => {
  listenerCount--
  if (listenerCount === 0) {
    document.removeEventListener('drop', dropEventListener)
  }
}

export const useDragEvents = ({
  quickKey,
  handleDrop,
  containerElement,
}: UseDragEventsParams): DragEvents => {
  const [dragStatus, setDragStatus] = useState<DragStatus | null>(null)

  useEffect(() => {
    register()
    return () => {
      unregister()
    }
  }, [])

  return useMemo(() => {
    if (!handleDrop) {
      return {}
    }

    if (!containerElement) {
      return {}
    }

    return {
      onDragStart: (e: DragEvent) => {
        if (quickKey.type === QuickKeyNodeType.BLANK) {
          return
        }
        e.dataTransfer.effectAllowed = 'move'
        draggedQuickKey = quickKey
        if (e.dataTransfer.setDragImage) {
          e.dataTransfer.setDragImage(containerElement, 5, 5)
        }
      },
      onDragEnter: (e: DragEvent) => {
        if (
          draggedQuickKey &&
          isValidDropDestination(quickKey, draggedQuickKey)
        ) {
          setDragStatus('valid_target')
        } else {
          setDragStatus('invalid_target')
        }
        e.preventDefault()
      },
      onDragLeave: (e: DragEvent) => {
        setDragStatus(null)
        e.preventDefault()
      },
      onDragOver: (e: DragEvent) => {
        e.preventDefault()
      },
      onDrop: (e: DragEvent) => {
        e.preventDefault()
        setDragStatus(null)
        if (!draggedQuickKey) {
          return
        }
        handleDrop({ dropped: draggedQuickKey, target: quickKey })
      },
      dragStatus,
    }
  }, [containerElement, dragStatus, handleDrop, quickKey])
}
